import PropTypes from "prop-types";

import ToggleButton from "shared/components/inputs/toggle-button/ToggleButton";
import Text from "shared/components/data-display/text/Text";
import RichText from "shared/components/data-display/text/RichText";

import { sendNotification } from "shared/components/feedback/notification/notification";
import useAccessGroupApi from "features/admin/access-groups/single/useAccessGroup";
import useAccessGroupPageAccessManagePermissionsApi from "features/admin/access-groups/single/useAccessGroupPageAccessManagePermissions";
import { useState } from "react";
import {
    StyledTitleText,
    ListWrapper,
    ListMainBlock,
    Wrapper,
    ListContent,
} from "./AccessGroupPageAccessManagePermissions.styles";

const ListContentCard = ({ name, description }) => {
    return (
        <ListContent spacing="One">
            <Text variant="Headline_4" color="Gray_1">
                {name}
            </Text>
            {description && (
                <RichText
                    variant="Descriptive_1_1"
                    color="Gray_4"
                >
                    {description.replaceAll(String.raw`\n`, "\n")}
                </RichText>
            )}
        </ListContent>
    );
};

const ToggleParent = ({
    parentToggleEnabled,
    parentToggleCode,
    handleOnChange,
}) => {
    const [inProgressParentToggle, setInProgressParentToggle] = useState(false);
    return (
        <ToggleButton
            disabled={inProgressParentToggle}
            checked={parentToggleEnabled}
            onChange={() =>
                handleOnChange(
                    parentToggleCode,
                    parentToggleEnabled,
                    setInProgressParentToggle
                )
            }
            label={["Off", "On"]}
        />
    );
};

const ToggleChild = ({
    parentToggleEnabled,
    childToggleEnabled,
    childToggleCode,
    handleOnChange,
}) => {
    const [inProgressChildToggle, setInProgressChildToggle] = useState(false);

    return (
        <ToggleButton
            disabled={inProgressChildToggle || !parentToggleEnabled}
            checked={childToggleEnabled}
            onChange={() =>
                handleOnChange(
                    childToggleCode,
                    childToggleEnabled,
                    setInProgressChildToggle
                )
            }
            label={["Off", "On"]}
        />
    );
};

const AccessGroupPageAccessManagePermissions = ({ groupId, pageId }) => {
    const { useAccessGroup } = useAccessGroupApi();
    const groupData = useAccessGroup(groupId);

    const {
        useAccessGroupPageAccessPageActionDetails,
        createAccessGroupPageAccessPageAction,
        deleteAccessGroupPageAccessPageAction,
    } = useAccessGroupPageAccessManagePermissionsApi();

    const pageActionDetails = useAccessGroupPageAccessPageActionDetails({
        groupId: groupId,
        pageId: pageId,
    });

    if (pageActionDetails.isLoading) {
        return <>Loading...</>;
    }

    if (pageActionDetails.isError) {
        return <>Error</>;
    }

    const handleOnChange = async (code, enabled, setInProgress) => {
        setInProgress(true);
        const modifier = enabled
            ? deleteAccessGroupPageAccessPageAction
            : createAccessGroupPageAccessPageAction;

        const allData = { code: code, groupId: groupId, pageId: pageId };

        await modifier.mutateAsync(allData);
        setInProgress(false);
        sendNotification(
            <>
                Action Permissions for{" "}
                <strong> {groupData.data.name} </strong> have been updated
            </>
        );
    };

    return (
        <>
            {pageActionDetails.data.length === 0 ? (
                <>No data found</>
            ) : (
                <Wrapper>
                    {pageActionDetails.data
                        .filter((a) => a.parentCode === null)
                        .map((parentData, parentIndex) => (
                            <ListMainBlock
                                spacing="Zero"
                                key={"block1" + parentIndex}
                            >
                                <StyledTitleText
                                    variant="Headline_2"
                                    color="Gray_1"
                                >
                                    {parentData.header}
                                </StyledTitleText>

                                <ListWrapper>
                                    <ListContentCard {...parentData} />
                                    <ToggleParent
                                        parentToggleEnabled={parentData.enabled}
                                        parentToggleCode={parentData.code}
                                        handleOnChange={handleOnChange}
                                    />
                                </ListWrapper>

                                {pageActionDetails.data
                                    .filter(
                                        (element, index, arr) =>
                                            parentData.code ===
                                                element.parentCode &&
                                            arr.findIndex(
                                                (d) =>
                                                    element.header === d.header
                                            ) === index
                                    )
                                    .map(
                                        (
                                            childDataParent,
                                            childDataParentIndex
                                        ) => (
                                            <ListMainBlock
                                                spacing="Zero"
                                                key={
                                                    "block2" +
                                                    childDataParentIndex
                                                }
                                                style={{ marginLeft: "64px" }}
                                            >
                                                <StyledTitleText
                                                    variant="Headline_2"
                                                    color="Gray_1"
                                                >
                                                    {childDataParent.header}
                                                </StyledTitleText>
                                                {pageActionDetails.data
                                                    .filter(
                                                        (d) =>
                                                            d.header ===
                                                            childDataParent.header
                                                    )
                                                    .map(
                                                        (
                                                            childData,
                                                            childIndex
                                                        ) => (
                                                            <ListWrapper
                                                                key={
                                                                    "block3" +
                                                                    childIndex
                                                                }
                                                            >
                                                                <ListContentCard
                                                                    {...childData}
                                                                />

                                                                <ToggleChild
                                                                    parentToggleEnabled={
                                                                        parentData.enabled
                                                                    }
                                                                    childToggleEnabled={
                                                                        childData.enabled
                                                                    }
                                                                    childToggleCode={
                                                                        childData.code
                                                                    }
                                                                    handleOnChange={
                                                                        handleOnChange
                                                                    }
                                                                />
                                                            </ListWrapper>
                                                        )
                                                    )}
                                            </ListMainBlock>
                                        )
                                    )}
                            </ListMainBlock>
                        ))}
                </Wrapper>
            )}
        </>
    );
};

ListContentCard.propTypes = {
    name: PropTypes.string,
    description: PropTypes.string,
};

ToggleParent.propTypes = {
    parentToggleEnabled: PropTypes.bool,
    parentToggleCode: PropTypes.string,
    handleOnChange: PropTypes.func,
};

ToggleChild.propTypes = {
    parentToggleEnabled: PropTypes.bool,
    childToggleEnabled: PropTypes.bool,
    childToggleCode: PropTypes.string,
    handleOnChange: PropTypes.func,
};

AccessGroupPageAccessManagePermissions.propTypes = {
    groupId: PropTypes.string,
    pageId: PropTypes.number,
};

export default AccessGroupPageAccessManagePermissions;
